import { useMemo, useCallback, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { useRouteMatch, useHistory, useLocation } from 'react-router-dom';
import { ascend, isEmpty, sortWith } from 'ramda';
import { useQueryBonuses } from '../../../hooks/API/useQueryBonuses';
import { brandSelector } from '../../../redux/reducers/app/params';
import { getAPI } from '../../../services/api';
import { contentAPIPrefixSelector } from '../../../redux/reducers/app/configuration';
import {
  BONUSES as BONUSES_LIST,
  BONUS_HISTORY as BONUSES_HISTORY,
  BONUS_PAGE,
} from '../../../constants/routeTemplates';
import { ENTITY, QUERY } from '../../../constants/content';
import { getBonusesCategoryPath } from './utils';
import { sdkErrorHandler } from '../../../utils';

export const ALL_BONUSES_CATEGORY = 'all-lc';
export const BONUSES_HISTORY_CATEGORY = 'history-lc';

export const INTERNAL_CATEGORIES = [
  ALL_BONUSES_CATEGORY,
  BONUSES_HISTORY_CATEGORY,
];

const allBonusesCategory = {
  name: 'All bonuses',
  icon: null, // Load from strapi
  categoryId: ALL_BONUSES_CATEGORY,
};
const bonusesHistoryCategory = {
  name: 'History',
  icon: null, // Load from strapi
  categoryId: BONUSES_HISTORY_CATEGORY,
};

export const QUERY_KEY_BONUS_CATEGORY = 'bonus-category-content';

export const useQueryBonusCategory = (categoryIds, queryOptions = {}) => {
  const brand = useSelector(brandSelector);
  const contentAPIPrefix = useSelector(contentAPIPrefixSelector);

  const query = {
    [QUERY.BRANDS]: brand.toUpperCase(),
  };
  const searchParams = new URLSearchParams(query);

  if (Array.isArray(categoryIds) && !isEmpty(categoryIds)) {
    // use sort to cache request with CF
    const sortedIds = sortWith([ascend((a) => a)])(categoryIds);

    sortedIds.forEach((id) => {
      if (!id || typeof id !== 'string') return;

      searchParams.append('categoryId', id);
    });
  }

  const url = `/${ENTITY.BONUS_CATEGORIES}?${searchParams}`;

  const fetch = ({ signal }) =>
    getAPI()
      .fetch(url, { baseUrl: contentAPIPrefix, signal })
      .catch(
        sdkErrorHandler({
          source: `fetch to ${url}`,
          queryOptions,
          baseUrl: contentAPIPrefix,
        })
      );

  return useQuery([QUERY_KEY_BONUS_CATEGORY, categoryIds], fetch, {
    ...queryOptions,
  });
};

export const getCategories = (bonuses = []) => {
  return (
    bonuses
      .map((bonus) => {
        return bonus?.categories ?? [];
      })
      .flat()
      .filter((category, index, array) => {
        return (
          array.findIndex((item) => category.categoryId === item.categoryId) ===
          index
        );
      })
      .sort((a, b) => a.name?.localeCompare(b.name)) || 1
  );
};

export const useBonusCategories = () => {
  const contentAPIPrefix = useSelector(contentAPIPrefixSelector);

  const { data, isSuccess } = useQueryBonuses({
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const queryBonusCategories = useQueryBonusCategory([
    ALL_BONUSES_CATEGORY,
    BONUSES_HISTORY_CATEGORY,
  ]);

  const categories = useMemo(() => {
    const category = queryBonusCategories?.data?.find(
      (item) => item.categoryId === ALL_BONUSES_CATEGORY
    );
    const iconUrl = category?.icon?.url;

    const allBonusesCategoryIcon = iconUrl
      ? `${contentAPIPrefix}${iconUrl}`
      : null;

    const category1 = queryBonusCategories?.data?.find(
      (item) => item.categoryId === BONUSES_HISTORY_CATEGORY
    );
    const iconUrl1 = category1?.icon?.url;

    const bonusesHistoryCategoryIcon = iconUrl1
      ? `${contentAPIPrefix}${iconUrl1}`
      : null;

    const categories = getCategories(data);

    return [
      { ...allBonusesCategory, icon: allBonusesCategoryIcon },
      ...categories,
      {
        ...bonusesHistoryCategory,
        icon: bonusesHistoryCategoryIcon,
      },
    ];
  }, [queryBonusCategories.data, data, contentAPIPrefix]);

  return {
    categories,
    isSuccess: isSuccess && queryBonusCategories.isSuccess,
  };
};

export const useCurrentBonusCategory = () => {
  const history = useHistory();
  const routerMatch = useRouteMatch();
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const categoryId = params.get('category');
  const { categories, isSuccess } = useBonusCategories();

  const [currentCategory, setCurrentCategory] = useState(allBonusesCategory);

  useEffect(() => {
    switch (routerMatch.path) {
      case BONUS_PAGE:
        return;
      case BONUSES_HISTORY:
        return setCurrentCategory(bonusesHistoryCategory);
      default:
        isSuccess &&
          setCurrentCategory(
            categories?.find?.(
              (category) => category.categoryId === categoryId
            ) ?? allBonusesCategory
          );
    }
  }, [routerMatch.path, categories, categoryId, setCurrentCategory, isSuccess]);

  useEffect(() => {
    if (!isSuccess || !categoryId) return;

    if (!categories.find((category) => category.categoryId === categoryId)) {
      history.replace(BONUSES_LIST);
    }
  }, [isSuccess, categories, history, categoryId]);

  const onCategoryChange = useCallback(
    (category) =>
      history.push(getBonusesCategoryPath(category.categoryId), { category }),
    [history]
  );

  return useMemo(
    () => ({
      categories,
      currentCategory,
      onCategoryChange,
    }),
    [categories, currentCategory, onCategoryChange]
  );
};
